import { ColumnStore } from "@bryntum/schedulerpro";
import SwivuSortColumn from "./SwivuSortColumn.jsx";
import { WIDGET_KEYS } from "../../other/constants/WidgetKeys.jsx";

import {
  defaultCellRenderer,
  geoLocationButtonUrlHelper,
  defaultGroupRenderer as groupRenderer
} from "../../other/utils/utils.jsx";
import { ICON_COL_DEF_WIDTH, SWIVU_TOOLTIP_TRIGGER_CLS } from "../../other/constants/OtherKeys.jsx";
import { LOCATION_GEO_BUTTON_CONFIG } from "../../other/constants/AddressGeoButtonConfig.jsx";

const getLocationIcon = (hasCordinates = false) => {
  const color = hasCordinates ? "text-[#0a88e9]" : "";
  return `<i class="fa-solid fa-location-dot text-[1.4em] ${color}"></i>`;
};

export class SwivuAddressColumn extends SwivuSortColumn {
  static get $name() {
    return "SwivuAddressColumn";
  }

  static get type() {
    return WIDGET_KEYS.swivuAddressColumn;
  }

  static get fields() {
    return ["swivuRecordField", "swivuPickerDisplayField"]; // register fields for swivuPickerField to operate
  }

  static get defaults() {
    return {
      flex: 1,
      groupRenderer,
      htmlEncode: false,
      width: 30,
      minWidth: ICON_COL_DEF_WIDTH,
      cellCls: "cursor-pointer",
      cls: "swivu-icon-column",
      editor: {
        type: WIDGET_KEYS.swivuAddressPicker,
        cls: "[&_input]:h-full", // css fix for height issue of input in columns
        placeholder: "Search location",
        /* The editor tries to automatically update record when user blurs or presses enter key, but we don't want that as we are using custom swivuLocationChange listener to update record so we are disabling the 'completeKey' & blurAction config.
        Note: The auto updating issue still persists because the grid is directly calling the completeEdit method in Editor which is causing the update in the record. See Task.jsx 'formattedAddress' setter to see the issue */
        completeKey: false,
        blurAction: null,
        listeners: {
          paint: ({ source }) => {
            const cellEditor = source.owner;
            const { record } = cellEditor;

            /* initialize swivuAddressPicker field */
            if (window.googleMapsLoaded) {
              source.init(record);
            } else {
              window.addEventListener("googleMapsLoaded", () => {
                source.init(record);
              });
            }
          },
          swivuLocationChange: (e) => {
            const { source } = e;
            const { owner: cellEditor } = source;
            const column = cellEditor.owner.getColumnFromElement(source.element); // Get column from grid
            column.updateCellRecord(cellEditor.record, e);
          }
        }
      }
    };
  }

  getAddress(record) {
    return record[this.swivuRecordField] || {}; // For phantom records address is null, therefore returning empty object
  }

  setAddress(record, value) {
    record[this.swivuRecordField] = value; // Update value in record.address field
  }

  updateCellRecord(rowRecord, e) {
    const { source, record: locationRecord, value } = e;
    source.owner.hide(); // We have to manually hide the editor as the change is triggered by swivuAddressPicker and it doesn't not hide the editor. If we don't hide the editor we will face internal crashes

    // Finally update record
    if (locationRecord) {
      const { formattedAddress, lat, lon } = locationRecord;
      this.setAddress(rowRecord, { formatted_address: formattedAddress, lat, lon });
    } else {
      this.setAddress(rowRecord, { formatted_address: value, lat: null, lon: null });
    }
  }

  renderer({ record }) {
    const { formatted_address, lat, lon } = this.getAddress(record);
    return `<div class="${SWIVU_TOOLTIP_TRIGGER_CLS} leading-[1.8em] w-full flex items-center gap-[0.8em]">
      ${getLocationIcon(lat && lon)}
      <span>${defaultCellRenderer({ value: formatted_address })}</span>
    </div>`;
  }

  headerRenderer({ column }) {
    return `<div class="flex gap-[0.8em] items-center [&_i]:leading-[2em]">
      ${getLocationIcon()}
      <span class="text-wrap">${column.text}</span>
    </div>`;
  }

  tooltipRenderer({ record, cellTooltip }) {
    const { lat, lon } = this.getAddress(record);

    cellTooltip.tip.minWidth = "auto";
    cellTooltip.tip.element.classList.add("swivu-contact-tooltip", "[&_.b-tooltip-content]:!p-0")

    /* if the lat & lon is not present in record then don't want to show tooltip, As the location is 'unresolved' */
    if (!(lat && lon)) {
      cellTooltip.hideDelay = 0;
      cellTooltip.tip.hide(); // Don't show tooltip
      return;
    }
    return {
      children: [
        {
          tag: "div",
          class: "swivu-contact-tooltip-content",
          children: [
            {
              tag: "div",
              class: "tooltip-contact-container",
              children: LOCATION_GEO_BUTTON_CONFIG.map(({ title, icon, url }) => {
                return {
                  tag: "a",
                  target: "_blank",
                  href: geoLocationButtonUrlHelper(url, { lat, lon }),
                  class: "contact-pill contact-phone group flex items-center justify-between no-underline w-full",
                  html: ` <span class="flex items-center gap-3"><img src='${icon}' class="w-[1.25em] h-[1.25em]" />${title} </span>
                  <i style="rotate: -45deg;" class="fa-solid fa-arrow-right-long opacity-0 group-hover:opacity-100 transition-opacity duration-200"></i>`
                };
              })
            }
          ]
        }
      ]
    };
  }
}

ColumnStore.registerColumnType(SwivuAddressColumn);
